import { DragDropContext } from '@hello-pangea/dnd'
import PropTypes from 'prop-types'
import { Component } from 'react'

import DraggableList from './draggableList'
import childrenSchema from './schemas/children'
import containerSchema from './schemas/container'

class DragAndDropBoard extends Component {
  getListItemType = () => this.props.listItemType || 'item'

  isMultiColumn = () =>
    this.props.children.length > 1 &&
    this.props.children.length ===
      this.props.children.filter(
        (child) => child.props === undefined && child.droppableId !== undefined && child.children !== undefined,
      ).length

  onDragEnd = (result, provided) => {
    const { destination, reason, source } = result
    const { onDragEnd } = this.props
    const listItemType = this.getListItemType()
    const startList = source.droppableId
    const endList = destination && destination.droppableId
    const startPosition = source.index + 1
    const endPosition = destination && destination.index + 1

    if (reason === 'CANCEL') {
      provided.announce(`Returning the ${listItemType} to its original position.`)
    } else {
      const announce = [`You have dropped the ${listItemType}.`]
      if (destination && (endList !== startList || endPosition !== startPosition)) {
        if (endList === startList) {
          announce.push(`It has moved from position ${startPosition} to ${endPosition}.`)
        } else {
          announce.push(
            `It has moved from list ${startList} in position ${startPosition} to list ${endList} in position ${endPosition}.`,
          )
        }
      }
      provided.announce(announce.join(' '))
    }

    if (window.navigator.vibrate) {
      // Cancel vibration
      window.navigator.vibrate(0)
    }

    onDragEnd(result, provided)
  }

  onDragStart = (start, provided) => {
    const { source } = start
    const { children, onDragStart } = this.props
    const listItemType = this.getListItemType()
    const startList = source.droppableId
    const startPosition = source.index + 1
    const announce = [`You have lifted the ${listItemType}`]

    if (this.isMultiColumn()) {
      const column = children.find((col) => col.droppableId === source.droppableId)
      announce.push(`from list ${startList} in position ${startPosition} of ${column.children.length}.`)
    } else {
      announce.push(`in position ${startPosition} of ${children.length}.`)
    }

    announce.push('Use the arrow keys to move, space bar to drop, and escape to cancel.')
    provided.announce(announce.join(' '))

    if (window.navigator.vibrate) {
      window.navigator.vibrate(100)
    }

    onDragStart(start, provided)
  }

  onDragUpdate = (update, provided) => {
    const { destination, source } = update
    const { onDragUpdate } = this.props
    const listItemType = this.getListItemType()
    const startList = source.droppableId
    const endList = destination && destination.droppableId
    const startPosition = source.index + 1
    const endPosition = destination && destination.index + 1

    if (destination) {
      const announce = [`You have moved the ${listItemType}`]
      if (endList === startList) {
        announce.push(`from position ${startPosition} to ${endPosition}.`)
      } else {
        announce.push(
          `from list ${startList} in position ${startPosition} to list ${endList} in position ${endPosition}.`,
        )
      }
      provided.announce(announce.join(' '))
    }

    onDragUpdate(update, provided)
  }

  render() {
    const { children, onBeforeDragStart, onDragEnd, onDragStart, onDragUpdate, ...props } = this.props

    return (
      <DragDropContext
        onBeforeDragStart={onBeforeDragStart}
        onDragEnd={this.onDragEnd}
        onDragStart={this.onDragStart}
        onDragUpdate={this.onDragUpdate}
      >
        {this.isMultiColumn() ? (
          children.map((child) => <DraggableList {...props} {...child} key={child.droppableId} />)
        ) : (
          <DraggableList {...props}>{children}</DraggableList>
        )}
      </DragDropContext>
    )
  }
}

DragAndDropBoard.propTypes = {
  children: PropTypes.oneOfType([PropTypes.arrayOf(childrenSchema), PropTypes.arrayOf(containerSchema)]).isRequired,
  // https://github.com/atlassian/react-beautiful-dnd#ondragend-required
  onDragEnd: PropTypes.func.isRequired,

  listItemType: PropTypes.string,

  // https://github.com/atlassian/react-beautiful-dnd/blob/master/docs/guides/responders.md#secondary
  onBeforeDragStart: PropTypes.func,
  // https://github.com/atlassian/react-beautiful-dnd#ondragstart-optional
  onDragStart: PropTypes.func,
  // https://github.com/atlassian/react-beautiful-dnd#ondragupdate-optional
  onDragUpdate: PropTypes.func,
}

DragAndDropBoard.defaultProps = {
  listItemType: '',
  onBeforeDragStart: Function.prototype,
  onDragStart: Function.prototype,
  onDragUpdate: Function.prototype,
}

export default DragAndDropBoard
